import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { apiUtils } from "../utils/apiUtils";

// Create the UserContext
const UserContext = createContext();

// Custom hook to use the UserContext
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

// UserProvider component
// Auth uses HttpOnly cookie (auth_token); token is never stored in JS-accessible storage.
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null); // Legacy: only set when Bearer token in response (API clients)
  const [isLoading, setIsLoading] = useState(true);

  // Function to get stored auth data (legacy: user from storage for display after refresh)
  const getStoredAuthData = () => {
    const localUser = localStorage.getItem("user");
    if (localUser) return { user: JSON.parse(localUser) };
    const sessionUser = sessionStorage.getItem("user");
    if (sessionUser) return { user: JSON.parse(sessionUser) };
    return null;
  };

  // Function to save auth data (user only; token stored in HttpOnly cookie by backend)
  const saveAuthData = useCallback((authData, persistent = false) => {
    const userData = authData.user;
    if (persistent) {
      localStorage.setItem("user", JSON.stringify(userData));
    } else {
      sessionStorage.setItem("user", JSON.stringify(userData));
    }
    setUser(userData);
    if (authData.token) setToken(authData.token); // Legacy: for API clients that need Bearer
  }, []);

  // Function to clear auth data
  const clearAuthData = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    setToken(null);
    setUser(null);
  }, []);

  // Function to login
  const login = useCallback(async (credentials, rememberMe = false) => {
    try {
      const response = await apiUtils.post("/api/users/login", credentials);
      const data = await response.json();

      saveAuthData(data, rememberMe);
      return data;
    } catch (error) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error("Failed to login");
    }
  }, [saveAuthData]);

  // Function to logout
  const logout = useCallback(async () => {
    try {
      // Optional: Call logout API if needed
      if (token) {
        await apiUtils.post("/api/users/logout", {});
      }
    } catch (error) {
      console.error("Logout API error:", error);
    } finally {
      clearAuthData();
    }
  }, [token, clearAuthData]);

  // Function to update user profile
  const updateUser = useCallback((updatedUserData) => {
    setUser(prev => {
      const newUser = { ...prev, ...updatedUserData };
      if (localStorage.getItem("user")) localStorage.setItem("user", JSON.stringify(newUser));
      if (sessionStorage.getItem("user")) sessionStorage.setItem("user", JSON.stringify(newUser));
      return newUser;
    });
  }, []);

  // Function to mark onboarding as completed
  const completeOnboarding = useCallback(async () => {
    try {
      const response = await apiUtils.post("/api/users/complete-onboarding", {});
      const data = await response.json();

      if (data.success) {
        // Update user state with new onboarding status
        updateUser({ onboardingCompleted: true });
        return data.user;
      } else {
        throw new Error(data.error || "Failed to complete onboarding");
      }
    } catch (error) {
      console.error("Error completing onboarding:", error);
      throw error;
    }
  }, [updateUser]);

  // Function to check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return !!user;
  }, [user]);

  // Initialize auth state: verify via /me (cookie sent automatically)
  useEffect(() => {
    const storedAuthData = getStoredAuthData();
    if (storedAuthData?.user) setUser(storedAuthData.user);

    const verifyAuth = async () => {
      try {
        const response = await apiUtils.get("/api/users/me");
        const data = await response.json();
        if (response.ok && data) {
          setUser(data);
          if (localStorage.getItem("user")) localStorage.setItem("user", JSON.stringify(data));
          else if (sessionStorage.getItem("user")) sessionStorage.setItem("user", JSON.stringify(data));
        } else {
          clearAuthData();
        }
      } catch {
        clearAuthData();
      } finally {
        setIsLoading(false);
      }
    };

    verifyAuth();
    // Mount-once: verifies the session cookie exactly once on load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for auth logout events from API utils
  useEffect(() => {
    const handleAuthLogout = () => {
      clearAuthData();
    };

    window.addEventListener('auth-logout', handleAuthLogout);
    return () => window.removeEventListener('auth-logout', handleAuthLogout);
    // Mount-once listener; clearAuthData is stable in practice and re-binding
    // the listener per render would be churn for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    logout,
    updateUser,
    completeOnboarding,
    clearAuthData,
  }), [user, token, isLoading, isAuthenticated, login, logout, updateUser, completeOnboarding, clearAuthData]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
